import React, { useMemo, useState } from "react";
import type { Pet } from "../../../common/types";
import { calcEVByRules } from "../../helpers/helpers";
import defenseMapJson from "../../helpers/pet_defense.json";

type DefenseMap = Record<string, { defense: number; score: number }>;
const DEFENSE_MAP: DefenseMap = defenseMapJson as DefenseMap;

const THRESHOLDS = [0.6, 0.7, 0.8]; // 60%, 70%, 80%
type SortKey = "ev" | "def" | "targetScore" | "odds";

export function Calculator({ pets, isDarkMode }: { pets: Pet[]; isDarkMode: boolean }) {
  const [selectedId, setSelectedId] = useState<number>(pets[0]?.id ?? 0);
  const [selectedThreshold, setSelectedThreshold] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("ev");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const selectedPet = useMemo(
    () => pets.find((p) => p.id === selectedId) ?? null,
    [pets, selectedId],
  );

  // 1) Build full candidate list and **rank by EV desc only**
  const rankedByEV = useMemo(() => {
    if (!selectedPet) return [];
    const ap = Number(selectedPet.attackPoints ?? 0);
    const myScore = Number(selectedPet.score ?? 0);

    const rows: {
      id: number;
      def: number;
      targetScore: number;
      odds: number;
      win: number;
      lose: number;
      ev: number;
    }[] = [];

    for (const [idStr, row] of Object.entries(DEFENSE_MAP)) {
      const id = Number(idStr);
      if (!Number.isFinite(id)) continue;

      const dp = Number(row.defense ?? 0);
      const targetScore = Number(row.score ?? 0);
      if (targetScore <= 0 || dp < 0) continue;

      const { odds, win, lose, ev } = calcEVByRules(myScore, ap, dp, targetScore);
      if (!Number.isFinite(ev)) continue;

      rows.push({ id, def: dp, targetScore, odds, win, lose, ev });
    }

    rows.sort((a, b) => b.ev - a.ev);
    return rows;
  }, [selectedPet]);

  // 2) Filter by win-rate (if any) and 3) **slice to 20** to lock membership
  const evTop20Pool = useMemo(() => {
    if (!rankedByEV.length) return [];
    const filtered =
      selectedThreshold !== null
        ? rankedByEV.filter((r) => r.odds >= selectedThreshold)
        : rankedByEV;
    return filtered.slice(0, 20);
  }, [rankedByEV, selectedThreshold]);

  // 4) Sort **within those 20** for display
  const visible = useMemo(() => {
    const arr = evTop20Pool.slice();
    if (sortKey === "ev") {
      // EV desc is already the base; only flip to asc if requested
      return sortOrder === "asc" ? arr.sort((a, b) => a.ev - b.ev) : arr;
    }
    return arr.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      return sortOrder === "asc" ? va - vb : vb - va;
    });
  }, [evTop20Pool, sortKey, sortOrder]);

  return (
    <div className="flex w-full flex-col items-center justify-center">
      <div
        className={`mb-4 mt-3 flex w-[21.5rem] flex-col rounded-lg border-2 p-3 ${
          isDarkMode
            ? "border-darkgreen bg-darkblue text-white [box-shadow:inset_0_-2px_#37b764]"
            : "border-brown bg-white [box-shadow:inset_0_-2px_#342e2e]"
        }`}
      >
        <div className="text-center text-lg font-semibold">Top 20 Bonk Targets</div>

        <div className="mb-2 mt-3 flex flex-col gap-3">
          {/* Pet selector + sort controls */}
          <div className="flex flex-row items-center justify-between gap-2">
            <select
              className={`rounded border px-2 py-1 text-sm ${
                isDarkMode
                  ? "border-darkgreen bg-darkblack text-white"
                  : "border-brown bg-white text-brown"
              }`}
              value={selectedId}
              onChange={(e) => setSelectedId(Number(e.target.value))}
            >
              {pets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} • {p.attackPoints}
                </option>
              ))}
            </select>

            <div className="flex items-center gap-2">
              <select
                value={sortKey}
                onChange={(e) => setSortKey(e.target.value as SortKey)}
                className={`mr-1 rounded border px-2 py-1 text-sm ${
                  isDarkMode
                    ? "border-darkgreen bg-darkblack text-white"
                    : "border-brown bg-white text-brown"
                }`}
              >
                <option value="ev">EV</option>
                <option value="def">Defense</option>
                <option value="targetScore">Score</option>
                <option value="odds">Odds</option>
              </select>
              <button onClick={toggleSortOrder} aria-label="Toggle sort order">
                <img
                  src={
                    sortOrder === "desc"
                      ? isDarkMode
                        ? "/imgs/descendingDark.svg"
                        : "/imgs/descending.svg"
                      : isDarkMode
                        ? "/imgs/ascendingDark.svg"
                        : "/imgs/ascending.svg"
                  }
                  alt={sortOrder === "desc" ? "Descending" : "Ascending"}
                  className="h-4 w-4"
                />
              </button>
            </div>
          </div>

          {/* Single-choice win-rate filter */}
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="inline-flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="winrate"
                checked={selectedThreshold === null}
                onChange={() => setSelectedThreshold(null)}
                className={`h-4 w-4 ${isDarkMode ? "accent-darkgreen" : "accent-brown"}`}
              />
              <span className={`${isDarkMode ? "text-white" : "text-brown"}`}>All</span>
            </label>
            {THRESHOLDS.map((t) => (
              <label key={t} className="inline-flex cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name="winrate"
                  checked={selectedThreshold === t}
                  onChange={() => setSelectedThreshold(t)}
                  className={`h-4 w-4 ${isDarkMode ? "accent-darkgreen" : "accent-brown"}`}
                />
                <span className={`${isDarkMode ? "text-white" : "text-brown"}`}>
                  ≥ {Math.round(t * 100)}%
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="max-h-[28rem] space-y-2 overflow-auto pr-1">
          {selectedPet &&
            visible.map((row, index) => {
              const { id, def, targetScore, odds, win, lose, ev } = row;
              const oddsPct = odds * 100;
              const lightColors = ["bg-skyblue", "bg-green", "bg-pink", "bg-gold", "bg-copper"];
              const darkColors = ["bg-darkblack", "bg-darkblue"];
              const bgClass = isDarkMode
                ? darkColors[index % darkColors.length]
                : lightColors[index % lightColors.length];

              return (
                <div
                  key={id}
                  className={`flex items-center justify-between rounded border px-3 py-2 text-sm ${
                    isDarkMode ? `border-darkgreen ${bgClass}` : `border-brown ${bgClass}`
                  }`}
                >
                  <div className="flex flex-col">
                    <div className="font-semibold">#{id}</div>
                    <div className="flex flex-row">
                      <img
                        src={isDarkMode ? "/imgs/shieldDark.svg" : "/imgs/shield.svg"}
                        alt=""
                        className="mr-1 mt-[.2rem] h-3 w-3"
                      />
                      {def} •
                      <img
                        src={isDarkMode ? "/imgs/slotDark.svg" : "/imgs/slot.svg"}
                        alt=""
                        className="ml-1 mr-1 mt-[.2rem] h-[.85rem] w-[.85rem]"
                      />
                      {Math.floor(targetScore).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div>Odds: {oddsPct.toFixed(1)}%</div>
                    <div>Win: +{win.toFixed(0)}</div>
                    <div>Lose: −{lose.toFixed(0)}</div>
                    <div className="font-semibold">
                      EV: {ev >= 0 ? "+" : ""}
                      {ev.toFixed(1)}
                    </div>
                    <a
                      href={`https://frenpet.xyz/pet/${id}`}
                      target="_blank"
                      rel="noreferrer"
                      className={`mt-1 inline-block underline ${isDarkMode ? "text-white" : "text-brown"}`}
                    >
                      View Pet
                    </a>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
